import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  DocumentIcon,
  ListBulletIcon,
  PhotoIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  INTERACTIVE_LIMITS,
  tInteractiveButton,
  tInteractiveListSection,
  tInteractiveOutbound,
} from '../../../../../../Chat.types';
import {
  safeInteractiveUrl,
  totalListRowCount,
} from '../../InteractiveAction.functions';
import PreviewSaveQuickReply from '../PreviewSaveQuickReply/PreviewSaveQuickReply.component';

// Live preview rendered from Formik state. Mirrors how Meta renders the
// interactive message on the recipient side (white incoming bubble with
// action pills below), so the seller can verify spacing, emojis, and which
// fields actually show up before sending. We deliberately use the recipient
// styling (white bg) — the goal is "what does the customer see".
export default function PreviewBubble() {
  const { t } = useTranslation(['chat/chat']);
  const { values } = useFormikContext<tInteractiveOutbound>();
  // Controls the WA-style "bottom sheet" that pops open over the preview
  // when the seller taps the list trigger button. Mirrors the recipient
  // experience — section titles, row title + description, radio circle.
  const [isListSelectorOpen, setIsListSelectorOpen] = useState(false);

  // Auto-dismiss the selector when the seller switches to a non-list
  // variant so the overlay doesn't linger over the button/cta_url preview.
  useEffect(() => {
    if (values.type !== 'list') setIsListSelectorOpen(false);
  }, [values.type]);

  const hasAnyContent =
    Boolean(values.header) ||
    Boolean(values.body?.trim()) ||
    Boolean(values.footer?.trim());

  return (
    <div className="InteractiveAction__Preview">
      <div className="InteractiveAction__Preview__Scrollable">
        <div className="InteractiveAction__Preview__Label">
          {t('interactive.preview.label')}
        </div>
        <div className="InteractiveAction__Preview__BubbleWrap">
          {hasAnyContent ? (
            <>
              <div className="InteractiveAction__Preview__Bubble">
                <PreviewHeader values={values} />
                {values.body?.trim() ? (
                  <p className="InteractiveAction__Preview__Body">
                    {values.body}
                  </p>
                ) : (
                  <p className="InteractiveAction__Preview__BodyPlaceholder">
                    {t('interactive.preview.bodyPlaceholder')}
                  </p>
                )}
                {values.footer?.trim() ? (
                  <p className="InteractiveAction__Preview__Footer">
                    {values.footer}
                  </p>
                ) : null}
              </div>
              <PreviewActions
                values={values}
                onOpenListSelector={() => setIsListSelectorOpen(true)}
              />
            </>
          ) : (
            <div className="InteractiveAction__Preview__Empty">
              {t('interactive.preview.empty')}
            </div>
          )}
        </div>

        <PreviewSaveQuickReply />
      </div>

      {values.type === 'list' && isListSelectorOpen && (
        <ListSelector
          values={values}
          onClose={() => setIsListSelectorOpen(false)}
        />
      )}
    </div>
  );
}

function PreviewHeader({ values }: { values: tInteractiveOutbound }) {
  const { t } = useTranslation(['chat/chat']);
  const header = values.header;
  if (!header) return null;

  if (header.type === 'text') {
    const text = header.text?.trim();
    if (!text) return null;
    return <p className="InteractiveAction__Preview__Header">{text}</p>;
  }

  if (header.type === 'image') {
    const safeLink = safeInteractiveUrl(header.image?.link);
    if (safeLink) {
      return (
        <img
          src={safeLink}
          alt={t('interactive.preview.imageAlt')}
          className="InteractiveAction__Preview__Image"
          referrerPolicy="no-referrer"
        />
      );
    }
    return (
      <div className="InteractiveAction__Preview__MediaPlaceholder">
        <PhotoIcon className="w-5 h-5" />
        <span>{t('interactive.header.types.image')}</span>
      </div>
    );
  }

  if (header.type === 'video') {
    return (
      <div className="InteractiveAction__Preview__MediaPlaceholder">
        <VideoCameraIcon className="w-5 h-5" />
        <span>{t('interactive.header.types.video')}</span>
      </div>
    );
  }

  if (header.type === 'document') {
    return (
      <div className="InteractiveAction__Preview__MediaPlaceholder">
        <DocumentIcon className="w-5 h-5" />
        <span>{t('interactive.header.types.document')}</span>
      </div>
    );
  }

  return null;
}

function PreviewActions({
  values,
  onOpenListSelector,
}: {
  values: tInteractiveOutbound;
  onOpenListSelector: () => void;
}) {
  const { t } = useTranslation(['chat/chat']);

  if (values.type === 'button') {
    const buttons = values.action.buttons.filter(
      (b: tInteractiveButton) => b.title.trim() !== '',
    );
    if (buttons.length === 0) return null;
    return (
      <div className="InteractiveAction__Preview__Actions">
        {buttons.map((btn, idx) => (
          <div
            key={idx}
            className="InteractiveAction__Preview__ReplyButton"
            title={btn.title}
          >
            <ArrowUturnLeftIcon className="w-4 h-4" />
            <span>{btn.title}</span>
          </div>
        ))}
      </div>
    );
  }

  if (values.type === 'cta_url') {
    const label = values.action.display_text.trim();
    if (!label) return null;
    return (
      <div className="InteractiveAction__Preview__Actions">
        <div className="InteractiveAction__Preview__CtaButton" title={label}>
          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          <span>{label}</span>
        </div>
      </div>
    );
  }

  if (values.type === 'list') {
    const label = values.action.button_text.trim();
    const totalRows = totalListRowCount(values.action.sections);
    const sectionsWithRows = values.action.sections.filter(
      (s: tInteractiveListSection) =>
        s.rows.some((r) => r.title.trim() !== ''),
    ).length;
    if (!label && totalRows === 0) return null;
    return (
      <div className="InteractiveAction__Preview__Actions">
        <button
          type="button"
          className="InteractiveAction__Preview__ListButton"
          onClick={onOpenListSelector}
          aria-label={t('interactive.preview.openSelector')}
        >
          <ListBulletIcon className="w-4 h-4" />
          <span className="InteractiveAction__Preview__ListButton__Label">
            {label || t('interactive.preview.listButtonFallback')}
          </span>
          <ChevronDownIcon className="w-4 h-4" />
        </button>
        <p className="InteractiveAction__Preview__ListSummary">
          {t('interactive.preview.listSummary', {
            sections: sectionsWithRows,
            rows: totalRows,
          })}
        </p>
      </div>
    );
  }

  return null;
}

// Bottom-sheet-style selector that pops over the preview area when the
// seller taps the list trigger. Mirrors the recipient UX — section titles
// as inline separators, rows with title + optional description, and a
// radio circle on the right for the "pick one" affordance. Empty rows
// (blank title) are hidden because WA wouldn't render them either.
function ListSelector({
  values,
  onClose,
}: {
  values: Extract<tInteractiveOutbound, { type: 'list' }>;
  onClose: () => void;
}) {
  const { t } = useTranslation(['chat/chat']);
  const title =
    values.action.button_text.trim() ||
    t('interactive.preview.listButtonFallback');

  // Yup caps total rows at LIST_ROWS_TOTAL_MAX (10) at submit time, but a
  // tampered cache or preload could theoretically sneak more rows into
  // Formik state. Enforce the same cap at render so the selector sheet
  // can never spill >10 rows into the DOM — defense in depth against
  // cheap-DoS shapes that wouldn't be caught until the seller clicks
  // Send.
  let remainingRowBudget = INTERACTIVE_LIMITS.LIST_ROWS_TOTAL_MAX;
  const visibleSections = values.action.sections
    .map((section: tInteractiveListSection) => {
      const visibleRows = section.rows.filter(
        (r) => r.title.trim() !== '',
      );
      const cappedRows = visibleRows.slice(0, remainingRowBudget);
      remainingRowBudget -= cappedRows.length;
      return {
        title: section.title?.trim() ?? '',
        rows: cappedRows,
      };
    })
    .filter((section) => section.rows.length > 0);

  return (
    // Root is the dim backdrop covering the preview pane; clicking it
    // closes the sheet (standard modal affordance). The inner __Sheet is
    // the actual centered dialog so the bubble behind stays partly
    // visible for context.
    <div
      className="InteractiveAction__Preview__ListSelector"
      onClick={onClose}
    >
      <div
        className="InteractiveAction__Preview__ListSelector__Sheet"
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="InteractiveAction__Preview__ListSelector__Header">
          <button
            type="button"
            className="InteractiveAction__Preview__ListSelector__Close"
            onClick={onClose}
            aria-label={t('interactive.preview.closeSelector')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <span className="InteractiveAction__Preview__ListSelector__Title">
            {title}
          </span>
        </div>

        <div className="InteractiveAction__Preview__ListSelector__Sections">
          {visibleSections.length === 0 ? (
            <p className="InteractiveAction__Preview__ListSelector__Empty">
              {t('interactive.preview.listSelectorEmpty')}
            </p>
          ) : (
            visibleSections.map((section, sIdx) => (
              <div
                key={sIdx}
                className="InteractiveAction__Preview__ListSelector__Section"
              >
                {section.title ? (
                  <p className="InteractiveAction__Preview__ListSelector__SectionTitle">
                    {section.title}
                  </p>
                ) : null}
                {section.rows.map((row, rIdx) => (
                  <div
                    key={rIdx}
                    className="InteractiveAction__Preview__ListSelector__Row"
                  >
                    <div className="InteractiveAction__Preview__ListSelector__Row__Text">
                      <p className="InteractiveAction__Preview__ListSelector__Row__Title">
                        {row.title}
                      </p>
                      {row.description?.trim() ? (
                        <p className="InteractiveAction__Preview__ListSelector__Row__Description">
                          {row.description}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className="InteractiveAction__Preview__ListSelector__Row__Radio"
                      aria-hidden="true"
                    />
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
